import { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import Accessibility from "highcharts/modules/accessibility";
import NoDataToDisplay from "highcharts/modules/no-data-to-display";
import HighchartsReact, {
  HighchartsReactRefObject,
} from "highcharts-react-official";
import { Button } from "react-bootstrap";
import {
  DESCRIPTION_MESSAGE,
  DOWNLOAD_MESSAGE,
  POPOUT_MESSAGE,
  REAL_TIME_RESERVE_MARGIN_SUFFIX,
} from "../Constants";
import { MisoColors } from "../colors";
import { ChartProps } from "../Interfaces/Charts";
import {
  RealTimeReserveMarginRecord,
  ReserveMarginRegion,
} from "../Interfaces/DataBroker";
import VCenteredModal from "../Components/VCenteredModal";
import { Download } from "../Utils/Downloader";
import { formatNumber } from "../Utils/StringFormatHelpers";
import "../styles/RealTimeReserveMargin.scss";

type MeasurementKey =
  | "reserveMargin"
  | "ltEq4hrReserveMargin"
  | "dispatchMax"
  | "btm"
  | "stranded"
  | "nsi"
  | "loadForecast"
  | "regReq"
  | "spinReq"
  | "mssgc";

type MeasurementDefinition = {
  key: MeasurementKey;
  name: string;
  color?: string;
};

const REGIONS: { key: ReserveMarginRegion; name: string }[] = [
  { key: "System", name: "System" },
  { key: "NorthCentral", name: "North+Central" },
  { key: "South", name: "South" },
];

const PRIMARY_MEASUREMENTS: MeasurementDefinition[] = [
  { key: "reserveMargin", name: "Reserve Margin", color: MisoColors.misoGreen },
  {
    key: "ltEq4hrReserveMargin",
    name: "4 Hour or Less Reserve Margin",
    color: MisoColors.misoBlue,
  },
];

const SYSTEM_MEASUREMENTS: MeasurementDefinition[] = [
  { key: "dispatchMax", name: "Dispatch Max", color: MisoColors.misoOrange },
  { key: "btm", name: "BTMG", color: MisoColors.purple },
  { key: "stranded", name: "Constraint Stranded", color: MisoColors.warmGray },
  { key: "nsi", name: "NSI", color: MisoColors.darkTeal },
  { key: "loadForecast", name: "Load Forecast", color: MisoColors.red },
  { key: "regReq", name: "Reg Req", color: MisoColors.ltMerlot },
  { key: "spinReq", name: "Spin Req", color: MisoColors.gray },
];

const REGIONAL_MEASUREMENTS: MeasurementDefinition[] = [
  { key: "dispatchMax", name: "Dispatch Max", color: MisoColors.misoOrange },
  { key: "btm", name: "BTMG", color: MisoColors.purple },
  { key: "stranded", name: "Constraint Stranded", color: MisoColors.warmGray },
  { key: "nsi", name: "NSI", color: MisoColors.darkTeal },
  { key: "loadForecast", name: "Load Forecast", color: MisoColors.red },
  { key: "mssgc", name: "MSSGC", color: MisoColors.gray },
];

const initialOptions: Highcharts.Options = {
  credits: {
    enabled: false,
  },
  chart: {
    reflow: true,
    zooming: {
      type: "x",
    },
    spacing: [12, 16, 12, 8],
    style: {
      fontFamily: "Lato, Arial, Helvetica, sans-serif",
    },
  },
  title: {
    text: undefined,
  },
  lang: {
    noData: "No data was received",
    thousandsSep: ",",
  },
  legend: {
    align: "center",
    verticalAlign: "bottom",
    itemStyle: {
      fontWeight: "normal",
    },
  },
  xAxis: {
    type: "datetime",
    title: {
      text: "Interval Ending EST",
    },
    crosshair: true,
  },
  yAxis: {
    title: {
      text: "MW",
    },
    labels: {
      formatter: function () {
        return Highcharts.numberFormat(this.value as number, 0, ".", ",");
      },
    },
  },
  tooltip: {
    shared: true,
    useHTML: true,
    xDateFormat: "%A, %b %e, %H:%M EST",
    pointFormatter: function () {
      const value =
        this.y === null || this.y === undefined
          ? "&mdash;"
          : `${formatNumber(this.y)} MW`;
      return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${value}</b><br />`;
    },
  },
  plotOptions: {
    series: {
      animation: {
        duration: 350,
      },
      marker: {
        enabled: false,
      },
      states: {
        hover: {
          lineWidthPlus: 0,
        },
      },
    },
  },
  series: [],
};

Accessibility(Highcharts);
NoDataToDisplay(Highcharts);

function parseEstTimestamp(value: string): number {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T?(\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (!match) {
    throw new Error(`Invalid reserve margin timestamp: ${value}`);
  }

  const [, year, month, day, hours, minutes, seconds = "0"] = match;
  return Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds),
  );
}

function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Invalid reserve margin value: ${String(value)}`);
  }
  return value;
}

function parseRecords(data: unknown): RealTimeReserveMarginRecord[] {
  if (!Array.isArray(data)) {
    throw new Error("The reserve margin response was not an array.");
  }

  return data.map((value, index) => {
    if (typeof value !== "object" || value === null) {
      throw new Error(`Reserve margin record ${index + 1} was not an object.`);
    }

    const record = value as Record<string, unknown>;
    const region = record.region;
    if (
      region !== "System" &&
      region !== "NorthCentral" &&
      region !== "South"
    ) {
      throw new Error(
        `Reserve margin record ${index + 1} has an unknown region.`,
      );
    }
    if (typeof record.intervalEndEst !== "string") {
      throw new Error(
        `Reserve margin record ${index + 1} has no interval end.`,
      );
    }

    return {
      id: typeof record.id === "number" ? record.id : index,
      intervalEndEst: record.intervalEndEst,
      region,
      regReq: toNullableNumber(record.regReq),
      spinReq: toNullableNumber(record.spinReq),
      suppReq: toNullableNumber(record.suppReq),
      loadForecast: toNullableNumber(record.loadForecast),
      dispatchMax: toNullableNumber(record.dispatchMax),
      btm: toNullableNumber(record.btm),
      nsi: toNullableNumber(record.nsi),
      stranded: toNullableNumber(record.stranded),
      mssgc: toNullableNumber(record.mssgc),
      reserveMargin: toNullableNumber(record.reserveMargin),
      ltEq4hrReserveMargin: toNullableNumber(record.ltEq4hrReserveMargin),
    };
  });
}

function getMeasurements(region: ReserveMarginRegion): MeasurementDefinition[] {
  return [
    PRIMARY_MEASUREMENTS[0],
    ...(region === "System" ? SYSTEM_MEASUREMENTS : REGIONAL_MEASUREMENTS),
  ];
}

function buildSeries(
  records: RealTimeReserveMarginRecord[],
  measurement: MeasurementKey,
): Highcharts.SeriesLineOptions[] {
  const definition = [
    ...PRIMARY_MEASUREMENTS,
    ...SYSTEM_MEASUREMENTS,
    ...REGIONAL_MEASUREMENTS,
  ].find((item) => item.key === measurement);
  if (!definition) {
    return [];
  }

  if (measurement === "reserveMargin") {
    return PRIMARY_MEASUREMENTS.map((item) => ({
      type: "line",
      name: item.name,
      color: item.color,
      lineWidth: 3,
      data: records.map(
        (record) =>
          [parseEstTimestamp(record.intervalEndEst), record[item.key]] as [
            number,
            number | null,
          ],
      ),
      zIndex: 5,
    }));
  }

  return [
    {
      type: "line",
      name: definition.name,
      color: definition.color,
      lineWidth: 3,
      data: records.map(
        (record) =>
          [parseEstTimestamp(record.intervalEndEst), record[measurement]] as [
            number,
            number | null,
          ],
      ),
      zIndex: 5,
    },
  ];
}

function getRangeLabel(records: RealTimeReserveMarginRecord[]): string {
  if (records.length === 0) {
    return "";
  }

  const sorted = [...records].sort(
    (left, right) =>
      parseEstTimestamp(left.intervalEndEst) -
      parseEstTimestamp(right.intervalEndEst),
  );
  const first = parseEstTimestamp(sorted[0].intervalEndEst);
  const last = parseEstTimestamp(sorted[sorted.length - 1].intervalEndEst);
  return `${Highcharts.dateFormat("%b %e, %Y %H:%M", first)} - ${Highcharts.dateFormat("%b %e, %Y %H:%M", last)} EST`;
}

export default function RealTimeReserveMargin(props: ChartProps) {
  const apiUrl = `${process.env.REACT_APP_PUBLIC_API_URL}${REAL_TIME_RESERVE_MARGIN_SUFFIX}`;
  const chartRef = useRef<HighchartsReactRefObject>(null);
  const [records, setRecords] = useState<RealTimeReserveMarginRecord[]>([]);
  const [selectedRegion, setSelectedRegion] =
    useState<ReserveMarginRegion>("System");
  const [selectedMeasurement, setSelectedMeasurement] =
    useState<MeasurementKey>("reserveMargin");
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalBody, setModalBody] = useState<React.ReactNode>(null);

  const regionRecords = useMemo(
    () =>
      records
        .filter((record) => record.region === selectedRegion)
        .sort(
          (left, right) =>
            parseEstTimestamp(left.intervalEndEst) -
            parseEstTimestamp(right.intervalEndEst),
        ),
    [records, selectedRegion],
  );
  const measurements = useMemo(
    () => getMeasurements(selectedRegion),
    [selectedRegion],
  );
  const selectedMeasurementIsValid = measurements.some(
    (item) => item.key === selectedMeasurement,
  );
  const activeMeasurement = selectedMeasurementIsValid
    ? selectedMeasurement
    : "reserveMargin";
  const options = useMemo<Highcharts.Options>(
    () => ({
      ...initialOptions,
      series: buildSeries(regionRecords, activeMeasurement),
    }),
    [activeMeasurement, regionRecords],
  );

  useEffect(() => {
    if (!selectedMeasurementIsValid) {
      setSelectedMeasurement("reserveMargin");
    }
  }, [selectedMeasurementIsValid]);

  useEffect(() => {
    let cancelled = false;

    async function fetchData(): Promise<void> {
      const chart = chartRef.current?.chart;
      chart?.showLoading();
      setError(null);

      try {
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(
            `Reserve margin request failed with status ${response.status}.`,
          );
        }
        const parsedRecords = parseRecords(await response.json());
        if (!cancelled) {
          setRecords(parsedRecords);
        }
      } catch (fetchError) {
        if (!cancelled) {
          const message =
            fetchError instanceof Error
              ? fetchError.message
              : "Unable to load reserve margin data.";
          setError(message);
        }
      } finally {
        chart?.hideLoading();
      }
    }

    fetchData();
    const interval = window.setInterval(fetchData, 1000 * 60 * 15);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [apiUrl]);

  useEffect(() => {
    const descriptionBody = (
      <>
        <p>
          A real-time outlook of reserve margin, refreshed every 15 minutes. Displays data for the current operating day. Once Day Ahead is cleared, it displays a rolling 24-hour outlook.
        </p>
        <p>
          This chart plots total online reserve margin and the reserve margin
          available within four hours or less. Use the measurement selector to
          inspect the component values underlying the reserve margin
          calculation.
        </p>
        <p>
          <strong>Systemwide Reserve Margin:</strong> Dispatch Max + BTMG -
          Constraint Stranded - NSI - (Load Forecast + Regulation Requirement +
          Spin Requirement)
        </p>
        <p>
          <strong>Regional Reserve Margin:</strong> Dispatch Max + BTMG -
          Constraint Stranded - NSI - (Load Forecast + MSSGC)
        </p>
        <p>
          <strong>Acronyms:</strong> BTMG is Behind the Meter Generation, NSI is
          Net Scheduled Interchange, and MSSGC is Most Severe Single Generation
          Contingency.
        </p>
        <p>
          <i>All calculations assume zero (0) Regional Dispatch Transfer (RDT). Total RDT flow can reach 3,000MW North-South or 2,500MW South-North.</i>
        </p>
      </>
    );
    const downloadBody = (
      <div className="download-modal">
        <Button
          className="download-btn"
          onClick={() => Download(apiUrl, "RealTimeReserveMargin", "json")}
        >
          Download JSON
        </Button>
      </div>
    );

    switch (props.buttonClicked) {
      case DESCRIPTION_MESSAGE:
        setModalBody(descriptionBody);
        setShowModal(true);
        break;
      case DOWNLOAD_MESSAGE:
        setModalBody(downloadBody);
        setShowModal(true);
        break;
      case POPOUT_MESSAGE: {
        const newWindow = window.open(
          "/charts/realtimereservemargin",
          "_blank",
          "width=800,height=600,noopener,noreferrer",
        );
        if (newWindow) {
          newWindow.opener = null;
        }
        break;
      }
      default:
        break;
    }
  }, [apiUrl, props.buttonClicked]);

  return (
    <div className="real-time-reserve-margin">
      <VCenteredModal
        show={showModal}
        onHide={() => setShowModal(false)}
        headercontent={<span>Real-Time Reserve Margin</span>}
        bodycontent={modalBody}
      />
      <div className="real-time-reserve-margin__controls">
        <div className="real-time-reserve-margin__control-row">
          <span className="real-time-reserve-margin__control-label">
            Region
          </span>
          <div
            className="real-time-reserve-margin__segmented-control"
            role="tablist"
            aria-label="Reserve margin region"
          >
            {REGIONS.map((region) => (
              <button
                key={region.key}
                type="button"
                role="tab"
                aria-selected={selectedRegion === region.key}
                className={selectedRegion === region.key ? "active" : ""}
                onClick={() => setSelectedRegion(region.key)}
              >
                {region.name}
              </button>
            ))}
          </div>
        </div>
        <div className="real-time-reserve-margin__control-row">
          <span className="real-time-reserve-margin__control-label">
            Measurement
          </span>
          <div
            className="real-time-reserve-margin__segmented-control"
            role="tablist"
            aria-label="Reserve margin measurement"
          >
            {measurements.map((measurement) => (
              <button
                key={measurement.key}
                type="button"
                role="tab"
                aria-selected={activeMeasurement === measurement.key}
                className={
                  activeMeasurement === measurement.key ? "active" : ""
                }
                onClick={() => setSelectedMeasurement(measurement.key)}
              >
                {measurement.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="real-time-reserve-margin__subtitle">
        {REGIONS.find((region) => region.key === selectedRegion)?.name}
        {" \u2022 "}
        {
          measurements.find(
            (measurement) => measurement.key === activeMeasurement,
          )?.name
        }
        {" \u2022 "}
        {getRangeLabel(regionRecords)}
      </div>
      {error && (
        <div className="real-time-reserve-margin__error" role="alert">
          {error}
        </div>
      )}
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
    </div>
  );
}
